/**
 * Interview Recording Manager
 *
 * Manages the complete recording lifecycle for interview sessions.
 * Handles starting, stopping, and switching between camera/screen share recordings.
 *
 * Follows Single Responsibility Principle - only handles recording management
 * Follows Dependency Inversion Principle - depends on abstractions (StreamRecorder)
 */

import { logger } from '@/lib/logging'
import { StreamRecorder } from '@/services/recording/StreamRecorder'
import {
  createRecordingStream,
  RecordingStreamConfig,
} from '@/services/recording/RecordingStreamBuilder'

/**
 * Interface for AI audio service
 * Allows dependency inversion - we depend on abstraction, not concrete implementation
 */
export interface IAIAudioService {
  getAIAudioStream(): MediaStream | null
}

export interface RecordingManagerConfig {
  microphoneStream: MediaStream | null
  aiAudioService: IAIAudioService | null
  mixingAudioContext?: AudioContext
  interviewId: string
  apiBaseUrl: string
}

export class InterviewRecordingManager {
  private recorder: StreamRecorder | null = null
  private recordingStream: MediaStream | null = null
  private mixingAudioContext: AudioContext | null = null
  private microphoneStream: MediaStream | null
  private aiAudioService: IAIAudioService | null
  private interviewId: string
  private apiBaseUrl: string
  private startTime: Date | null = null

  constructor(config: RecordingManagerConfig) {
    this.microphoneStream = config.microphoneStream
    this.aiAudioService = config.aiAudioService
    this.mixingAudioContext = config.mixingAudioContext || null
    this.interviewId = config.interviewId
    this.apiBaseUrl = config.apiBaseUrl
  }

  /**
   * Starts recording with the provided video source
   */
  public async startRecording(
    videoSource: MediaStream,
    includeAIAudio: boolean = true
  ): Promise<void> {
    try {
      if (!this.startTime) {
        this.startTime = new Date()
      }
      // Validate video source before creating recording stream
      const videoTracks = videoSource.getVideoTracks()
      if (videoTracks.length === 0) {
        throw new Error('Video source has no video tracks')
      }

      const videoTrack = videoTracks[0]
      if (videoTrack.readyState !== 'live') {
        logger.warn('Video track not in live state', {
          readyState: videoTrack.readyState,
          enabled: videoTrack.enabled,
          label: videoTrack.label,
        })
      }

      // Ensure video track is enabled
      if (!videoTrack.enabled) {
        videoTrack.enabled = true
        logger.info('Enabled video track for recording')
      }

      const streamConfig: RecordingStreamConfig = {
        videoSource,
        microphoneSource: this.microphoneStream,
        aiAudioService: includeAIAudio ? this.aiAudioService : null,
        includeAIAudio,
        mixingAudioContext: this.mixingAudioContext || undefined,
      }

      this.recordingStream = await createRecordingStream(streamConfig)

      // Create or reuse mixing AudioContext for future use
      if (!this.mixingAudioContext) {
        this.mixingAudioContext = new (window.AudioContext || (window as any).webkitAudioContext)()
      }

      // Validate stream before recording
      const recordingVideoTracks = this.recordingStream.getVideoTracks()
      if (recordingVideoTracks.length === 0) {
        throw new Error('No video tracks in recording stream')
      }

      // Log video track details for debugging
      const recordingVideoTrack = recordingVideoTracks[0]
      logger.info('Video track details', {
        trackId: recordingVideoTrack.id,
        trackLabel: recordingVideoTrack.label,
        readyState: recordingVideoTrack.readyState,
        enabled: recordingVideoTrack.enabled,
        settings: recordingVideoTrack.getSettings
          ? {
              width: recordingVideoTrack.getSettings().width,
              height: recordingVideoTrack.getSettings().height,
              frameRate: recordingVideoTrack.getSettings().frameRate,
            }
          : 'not available',
      })

      // Stream chunks continuously to backend storage to avoid holding
      // the full interview recording in browser memory until the end.
      if (!this.recorder) {
        this.recorder = new StreamRecorder({
          mediaStream: this.recordingStream,
          interviewId: this.interviewId,
          apiBaseUrl: this.apiBaseUrl,
          onStatusChange: (status) => logger.info(`Recording status: ${status}`),
          onError: (err) => logger.error('Recording error', { error: String(err) }),
        })
        await this.recorder.start()
      } else {
        await this.recorder.switchStream(this.recordingStream)
      }

      logger.info('Recording started/switched', {
        videoTracks: this.recordingStream.getVideoTracks().length,
        audioTracks: this.recordingStream.getAudioTracks().length,
        includeAIAudio,
      })
    } catch (err) {
      logger.error('Failed to start recording', { error: String(err) })
      // Clean up any resources that might have been created before the error
      this.cleanup()
      throw err
    }
  }

  /**
   * Stops the current recording
   * Returns multipart upload metadata for server-side completion.
   */
  public async stopRecording(): Promise<{
    uploadMetadata: {
      uploadId: string
      key: string
      parts: Array<{ PartNumber: number; ETag: string }>
    } | null
    recordingMetadata: {
      startTime: Date
      endTime: Date
      duration: number
      mimeType: string
    } | null
  }> {
    try {
      const endTime = new Date()
      const duration = this.startTime ? endTime.getTime() - this.startTime.getTime() : 0

      const recordingMetadata = {
        startTime: this.startTime || new Date(),
        endTime,
        duration,
        mimeType: 'video/webm',
      }

      if (this.recorder) {
        const result = await this.recorder.stop()
        this.recorder = null

        return {
          uploadMetadata: result || null,
          recordingMetadata: result ? recordingMetadata : null,
        }
      }

      return {
        uploadMetadata: null,
        recordingMetadata: null,
      }
    } catch (err) {
      logger.error('Failed to stop recording', { error: String(err) })
      return {
        uploadMetadata: null,
        recordingMetadata: null,
      }
    }
    // Note: Cleanup is handled by caller after extracting metadata
  }

  /**
   * Switches recording to a new video source (camera or screen share)
   */
  public async switchVideoSource(
    newVideoSource: MediaStream,
    includeAIAudio: boolean = true
  ): Promise<void> {
    // Just call startRecording which handles switching
    await this.startRecording(newVideoSource, includeAIAudio)
  }

  /**
   * Cancels the current recording without saving
   */
  public cancelRecording(): void {
    if (this.recorder?.isRecordingActive) {
      void this.recorder.cancel()
    }
    this.cleanup()
    logger.info('Recording cancelled')
  }

  /**
   * Checks if currently recording
   */
  public isRecording(): boolean {
    return this.recorder?.isRecordingActive || false
  }

  /**
   * Gets the current recording duration in milliseconds
   */
  public getDuration(): number {
    return 0 // StreamRecorder doesn't track duration yet
  }

  public updateMicrophoneStream(stream: MediaStream | null): void {
    this.microphoneStream = stream
  }

  public updateAIAudioService(service: IAIAudioService | null): void {
    this.aiAudioService = service
  }

  public cleanup() {
    if (this.recordingStream) {
      this.recordingStream.getTracks().forEach((track) => track.stop())
      this.recordingStream = null
    }
    this.recorder = null

    if (this.mixingAudioContext) {
      this.mixingAudioContext.close().catch((err) => {
        logger.warn('Error closing mixing AudioContext', { error: String(err) })
      })
      this.mixingAudioContext = null
    }
  }
}
